import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col, Table, Input, Space, Button, Tooltip, Typography, Tag, Badge } from 'antd';
import Utils from '../../misc/Utils';
import Highlighter from 'react-highlight-words';
import { SearchOutlined } from '@ant-design/icons';

function ItemAnalysisAnswers(props) {

   const [searchText, setSearchText] = React.useState("");
   const [searchedColumn, setSearchedColumn] = React.useState("");
   const [listLoading, setListLoading] = useState(false);
   const [studentList, setStudentList] = useState([]);

   // Filtering students with correct and incorrect answers
   const correctStudents = studentList.filter(student =>
      getTagColor(student.answer?.toUpperCase(), props.correct_answer, props.question_type) === 'green'
   );
   const incorrectStudents = studentList.filter(student =>
      getTagColor(student.answer?.toUpperCase(), props.correct_answer, props.question_type) === 'red'
   );

   // Filter state: "all", "correct", "incorrect"
   const [filter, setFilter] = useState("all");

   let filteredList = studentList;
   if (filter === "correct") {
      filteredList = correctStudents;
   } else if (filter === "incorrect") {
      filteredList = incorrectStudents;
   }

   useEffect(() => {
      Utils.verifyJwt();

      getAllData(props.id, props.assign_id, props.assessment_type);
   }, []);

   const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   };

   const handleReset = clearFilters => {
      clearFilters();
      setSearchText("");
   };

   const getColumnSearchProps = dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               ref={node => {
                  //this.searchInput(node);
               }}
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}>
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  // onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
            </Space>
         </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      // onFilterDropdownVisibleChange: visible => {
      //    if (visible) {
      //       setTimeout(() => this.searchInput.select(), 100);
      //    }
      // },
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   });

   const columns = [
      {
         title: <div style={{ textAlign: 'center' }}>Student Name</div>,
         key: 'student_name',
         ...getColumnSearchProps('student_name'),
         ellipsis: {
            showTitle: false,
         },
         render: payload => (
            <Tooltip placement="topLeft" title={payload.student_name}>
               <Tag
                  style={{ fontSize: 12, cursor: 'default' }}
                  color={getTagColor(payload.answer.toUpperCase(), props.correct_answer, props.question_type)}
               >
                  {payload.student_name.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase())}
               </Tag>
            </Tooltip>
         ),
         width: '22%'
      },
      {
         title: 'Grade Level',
         key: 'grade_level',
         ...getColumnSearchProps('grade_level'),
         ellipsis: {
            showTitle: false,
         },
         render: payload => (
            <Typography.Text>
               {payload.grade_level}
            </Typography.Text>
         ),
         width: '15%'
      },
      {
         title: 'Section',
         key: 'section',
         ...getColumnSearchProps('section'),
         ellipsis: {
            showTitle: false,
         },
         render: payload => (
            <Typography.Text>
               {payload.section}
            </Typography.Text>
         ),
         width: '23%'
      },
      {
         title: 'Answer',
         key: 'answer',
         ...getColumnSearchProps('answer'),
         render: payload => (
            <div style={{ height: "100%", width: "100%", textAlign: "left", wordWrap: "break-word" }}>
               <Typography.Text>{highLightNoAnswer(getProperAnswer(payload.answer.toUpperCase(), props.question_type))}</Typography.Text>
            </div>
         ),
         width: '30%'
      },

   ].filter(item => !item.hidden);

   const getAllData = async (id, assign_id, at) => {
      setListLoading(true);

      try {
         const [answersRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_ASSESSMENT}/getstudentanswers`, { params: { id: assign_id, at, qi: id } })
         ]);

         // ---- ASSESSMENT DATA CHECK ----
         if (answersRes && answersRes.data && typeof answersRes.data === "object") {
            const data = answersRes.data;
            setStudentList(data || []);
            setListLoading(false);
         } else {
            console.warn("Assessment API returned no valid data");
            setListLoading(false);
         }
      } catch (error) {
         console.error("Error fetching assessment/analysis:", error);
         setListLoading(false);
         // Optionally set error state for UI alert
         // setError("Failed to load data. Please try again later.");
      }
   };

   function getProperAnswer(answer, question_type) {
      var retVal = answer !== "" ? answer : "No Answer";
      var ans = answer.split(",");

      if (question_type === "multiple_choice") {
         const cleaned = ans.map(v => v.trim());
         var idx = cleaned.indexOf("1");

         if (idx === -1)
            retVal = "No Answer";
         else
            retVal = String.fromCharCode(65 + idx); // Convert index to letter (0 -> A, 1 -> B, etc.)
      }

      if (question_type === "true_or_false") {
         retVal = "No Answer";

         if (ans[0] === "1")
            retVal = "True";

         if (ans[1] === "1")
            retVal = "False";
      }

      if (question_type === "multiple_answer" || question_type === "multiple_answer_image") {
         const cleaned = ans.map(v => v.trim());
         var letters = [];
         cleaned.forEach((val, index) => {
            if (val === "1") {
               letters.push(String.fromCharCode(65 + index));
            }
         });

         if (letters.length === 0)
            retVal = "No Answer";
         else
            retVal = letters.join(", ");
      }

      return retVal;
   }

   function getTagColor(answer, correct_answer, question_type) {
      var retVal = 'red';

      if (question_type === "short_answer") {
         var correctAnswer = correct_answer.split("~|~");
         const found = correctAnswer.map(v => v.trim().toUpperCase()).includes(answer.trim().toUpperCase());

         if (found)
            retVal = 'green';
      } else {

         if (answer === correct_answer.replaceAll("~|~", ","))
            retVal = 'green';
      }

      return retVal;
   }

   function highLightNoAnswer(answer) {
      // return answer === "No Answer" ? <span style={{ backgroundColor: '#ffc069', padding: '0' }}>No Answer</span> : answer;
      return answer === "No Answer" ? <Tag color='#4ABEC7'>No Answer</Tag> : answer;
   }

   return (
      <>
         <Row gutter={[16, 16]} style={{ marginBottom: 16, display: "flex", justifyContent: 'center', gap: '12px' }}>
            <Col>
               <Badge
                  count={studentList.length}
                  style={{
                     backgroundColor: "blue",
                     cursor: "pointer",
                     fontSize: 12,
                     // padding: "0 12px"
                  }}
                  onClick={() => setFilter("all")}
                  overflowCount={999}
               // showZero
               >
                  <Tag color={"blue"} style={{ cursor: studentList.length > 0 ? 'pointer' : 'default' }} onClick={() => setFilter("all")}>All</Tag>
               </Badge>
            </Col>
            <Col>
               <Badge
                  count={correctStudents.length}
                  style={{
                     backgroundColor: "green",
                     cursor: "pointer",
                     fontSize: 12,
                     // padding: "0 12px"
                  }}
                  onClick={() => setFilter("correct")}
                  overflowCount={999}
               >
                  <Tag color={"green"} style={{ cursor: studentList.length > 0 ? 'pointer' : 'default' }} onClick={() => setFilter("all")}>Correct</Tag>
               </Badge>
            </Col>
            <Col>
               <Badge
                  count={incorrectStudents.length}
                  style={{
                     backgroundColor: "red",
                     cursor: "pointer",
                     fontSize: 12,
                     // padding: "0 12px"
                  }}
                  onClick={() => setFilter("incorrect")}
                  overflowCount={999}
               >
                  <Tag color={"red"} style={{ cursor: studentList.length > 0 ? 'pointer' : 'default' }} onClick={() => setFilter("all")}>Incorrect</Tag>
               </Badge>
            </Col>
         </Row>
         <Row>
            <Col span={24}>
               <div className='student_list_container'>
                  <Table
                     rowKey={'uid'}
                     loading={listLoading}
                     size='small'
                     columns={columns}
                     dataSource={filteredList}
                     pagination={false}
                     scroll={{
                        x: '500px',
                        y: 'calc(100vh - 28.5rem)',
                     }}
                     footer={() =>
                        <Row style={{ width: "100%" }}>
                           <Col style={{ width: "30%" }}>{'Total Records: ' + filteredList.length}</Col>
                           <Col style={{ width: "70%", textAlign: "right" }}>
                              {/* {'Correct Answer: ' + props.correct_answer} */}
                           </Col>
                        </Row>
                     }
                  />
               </div>
            </Col>
         </Row>
      </>
   )
}

export default ItemAnalysisAnswers;
